import React, { ReactNode, useMemo } from 'react';
import { Grid, Box, makeStyles } from '@material-ui/core';
import { Check } from 'react-feather';
import {
  moneyFormat,
  colors,
  CaptionText,
  H4Text,
  SubTitle1Text,
} from 'design-system';
import {
  Quotation,
  QuotationCoverage,
  CoverageCatalog,
} from 'data-layer-cr/dist/models';
import {
  endorsementCoverages,
  fullDataForCode,
} from 'data-layer-cr/dist/functions/coverages';
import InformationCardLayout from './InformationCardLayout';

const { formatMoney } = moneyFormat;

type EndorsementDetail = {
  code: string;
  name: string;
  value: string;
};

type Props = {
  quotation: Quotation;
  productCoverages: QuotationCoverage[];
  coverageCatalogs: CoverageCatalog[];
  additionalBenefits?: CoverageCatalog[];
};

const useStyles = makeStyles(() => ({
  fakeRadio: {
    width: 24,
    height: 24,
    marginRight: 10,
  },
  capitalize: {
    textTransform: 'capitalize',
  },
}));

type BenefitsRowProps = {
  children: ReactNode;
};

export const BenefitsRow = ({ children }: BenefitsRowProps): JSX.Element => {
  const classes = useStyles();
  return (
    <Grid item xs={12} data-testid="benefits-row">
      <Grid container alignItems="center">
        <Box
          alignItems="center"
          justifyContent="center"
          className={classes.fakeRadio}
        >
          <Check color={colors.bright_turquoise} size={25} />
        </Box>
        <Box
          sx={{
            width: 'calc(100% - 35px)',
          }}
        >
          {children}
        </Box>
      </Grid>
    </Grid>
  );
};

const BenefitsInformationCard = ({
  quotation,
  coverageCatalogs,
  productCoverages,
  additionalBenefits = [],
}: Props): JSX.Element => {
  const classes = useStyles();
  const endorsementResume = useMemo((): EndorsementDetail[] => {
    const resume: EndorsementDetail[] = [];
    const endorsementCoverageList = endorsementCoverages(coverageCatalogs);
    productCoverages.forEach((coverage) => {
      const endorsement = fullDataForCode(
        endorsementCoverageList,
        coverage.coverageId,
      );
      if (endorsement) {
        resume.push({
          code: endorsement.code,
          name: endorsement.name.toLowerCase(),
          value: formatMoney(
            endorsement.maxCost,
            false,
            quotation.quotationProfile.typeOfMoney,
          ),
        });
      }
    });
    return resume;
  }, [coverageCatalogs, productCoverages, quotation]);

  return (
    <InformationCardLayout
      data-testid="benefits-information-card"
      iconSrc="svg/heart.svg"
      iconAlt="heart_icon"
      title="Beneficios de la póliza"
    >
      <Box my={2}>
        <BenefitsRow data-testid="ensorment-assa-movil">
          <Box>
            <img src="svg/assa_movil.svg" alt="assa_movil" />
            <CaptionText>Asistencia vial exclusiva</CaptionText>
          </Box>
        </BenefitsRow>
      </Box>

      {additionalBenefits.map((ab) => (
        <Box my={2} key={ab.code}>
          <BenefitsRow data-testid={`endorsemente-additional-${ab.code}`}>
            <H4Text className={classes.capitalize}>{ab.name}</H4Text>
          </BenefitsRow>
        </Box>
      ))}

      {endorsementResume.map((endorsement) => (
        <BenefitsRow
          key={endorsement.code}
          data-testid={`endorsemente-${endorsement.code}`}
        >
          <Grid container justifyContent="space-between" alignItems="baseline">
            <H4Text className={classes.capitalize}>{endorsement.name}</H4Text>
            <SubTitle1Text>{endorsement.value}</SubTitle1Text>
          </Grid>
        </BenefitsRow>
      ))}
    </InformationCardLayout>
  );
};

export default BenefitsInformationCard;
